import React, { FC, useCallback, useState } from 'react';
import ChipGroup from './ChipGroup';
import { Icons } from '@silverfort/sf-components';
import { AUTH_TYPES_TO_SHOW, DIRECTORY_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { PROTOCOL_TO_DETAILS_MAP } from '../consts/policy';
import { Protocols } from '../../../../types/policies';
import { usePolicyTemplatesContext } from '../context/PolicyTemplatesContext';
import { PolicyTemplateRisk } from '../types/risk';

export interface TemplateChipsProps {
    spns?: string[];
    authTypes: string[];
    protocols?: Protocols[];
    risks?: string[];
    showLabels?: boolean;
    hideSpnChips?: boolean;
}

const getAuthTypesValues = (authTypes: string[]) => {
    return authTypes.map((type) => {
        if (type === DIRECTORY_TYPES.desktop_mfa.key) {
            return AUTH_TYPES_TO_SHOW.DESKTOP.label;
        }

        return Object.values(DIRECTORY_TYPES).find(({ key }) => type === key)?.displayName ?? '';
    });
};

const getRisksValues = (risks: string[], templateRisks: PolicyTemplateRisk[]): string[] => {
    return risks
        .map((risk) => templateRisks.find((templateRisk) => templateRisk.id === risk)?.name ?? '')
        .filter(Boolean);
};

const getProtocolValues = (protocols: Protocols[]) => {
    return protocols.reduce<string[]>((protocolValues, currentProtocol) => {
        if (currentProtocol === Protocols.KERBEROS_NTLM) {
            return [...protocolValues, 'Kerberos', 'NTLM'];
        }

        return [...protocolValues, PROTOCOL_TO_DETAILS_MAP[currentProtocol]];
    }, []);
};

const TemplateChips: FC<TemplateChipsProps> = ({ spns, authTypes, protocols, risks, showLabels = false, hideSpnChips = false }) => {
    const [protectFinishedSizing, setProtectFinishedSizing] = useState(false);
    const [authTypesFinishedSizing, setAuthTypesFinishedSizing] = useState(false);
    const [protocolFinishedSizing, setProtocolFinishedSizing] = useState(false);
    const shouldRenderAuthTypes = protectFinishedSizing || !spns;
    const finishRenderAuthTypes = !shouldRenderAuthTypes || (shouldRenderAuthTypes && authTypesFinishedSizing);
    const shouldRenderProtocols = finishRenderAuthTypes && protocols;
    const { risks: templateRisks } = usePolicyTemplatesContext();
    const handleProtectResizing = useCallback(() => {
        setProtectFinishedSizing(false);
    }, []);

    const handleAuthTypesResizing = useCallback(() => {
        setAuthTypesFinishedSizing(false);
    }, []);

    const handleProtocolResizing = useCallback(() => {
        setProtocolFinishedSizing(false);
    }, []);

    const handleProtectFinishSizing = useCallback(() => {
        setProtectFinishedSizing(true);
    }, []);

    const handleAuthTypesFinishSizing = useCallback(() => {
        setAuthTypesFinishedSizing(true);
    }, []);

    const handleProtocolFinishSizing = useCallback(() => {
        setProtocolFinishedSizing(true);
    }, []);

    return (
        <div className="flex flex-wrap gap-2 max-w-full">
            {!hideSpnChips && !!spns && spns.length > 0 && (
                <ChipGroup
                    shouldFitParent
                    label={showLabels ? 'Protecting' : undefined}
                    icon={Icons.Shield as FC<React.SVGProps<SVGSVGElement>>}
                    values={spns}
                    onResizing={handleProtectResizing}
                    onFinishSizing={handleProtectFinishSizing}
                />
            )}
            {shouldRenderAuthTypes && (
                <ChipGroup
                    shouldFitParent
                    label={showLabels ? 'Authentication' : undefined}
                    icon={Icons.Authentication as FC<React.SVGProps<SVGSVGElement>>}
                    values={getAuthTypesValues(authTypes)}
                    onResizing={handleAuthTypesResizing}
                    onFinishSizing={handleAuthTypesFinishSizing}
                />
            )}
            {shouldRenderProtocols && (
                <ChipGroup
                    shouldFitParent
                    label={showLabels ? 'Protocol' : undefined}
                    icon={Icons.DocCog as FC<React.SVGProps<SVGSVGElement>>}
                    values={getProtocolValues(protocols)}
                    onResizing={handleProtocolResizing}
                    onFinishSizing={handleProtocolFinishSizing}
                />
            )}
            {risks && protocolFinishedSizing && (
                <ChipGroup
                    shouldFitParent
                    label={showLabels ? 'Risk Involved' : undefined}
                    icon={Icons.Siren as FC<React.SVGProps<SVGSVGElement>>}
                    values={getRisksValues(risks, templateRisks)}
                />
            )}
        </div>
    );
};

export default TemplateChips;
